'use client';
import React, { useState, useEffect } from 'react';

interface ProfileSetupData {
  currentWeight: string;
  targetWeight: string;
  heightCm: string;
  age: string;
  gender: string;
  activityLevel: string;
  targetCalories: string;
  targetProtein: string;
  targetCarbs: string;
  targetFat: string;
  goalType: string;
  goalTitle: string;
  goalTargetValue: string;
  goalTargetDate: string;
  setupComplete: boolean;
  goalStatus: 'active' | 'achieved' | 'failed';
}

const ACTIVITY_LEVELS = [
  { value: 'sedentary', label: 'Sedentary', desc: 'Little/no exercise' },
  { value: 'light', label: 'Light', desc: '1–3 days/week' },
  { value: 'moderate', label: 'Moderate', desc: '3–5 days/week' },
  { value: 'active', label: 'Active', desc: '6–7 days/week' },
  { value: 'very_active', label: 'Very Active', desc: 'Twice daily' },
];

const GOAL_TYPES = [
  { value: 'weight_loss', label: 'Lose Weight' },
  { value: 'body_composition', label: 'Body Fat %' },
  { value: 'strength', label: 'Strength Goal' },
  { value: 'habit', label: 'Daily Habit' },
  { value: 'custom', label: 'Custom Goal' },
];

interface HeroSetupFormProps {
  profileKey: 'theo' | 'tracy';
  profileName: string;
  accentClass: string;
  borderClass: string;
  onComplete: () => void;
  onCancel: () => void;
}

const STORAGE_KEY = (profile: string) => `forge_setup_${profile}`;

export function getProfileSetup(profile: string): ProfileSetupData | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY(profile));
    if (!raw) return null;
    return JSON.parse(raw) as ProfileSetupData;
  } catch {
    return null;
  }
}

export function saveProfileSetup(profile: string, data: ProfileSetupData): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(STORAGE_KEY(profile), JSON.stringify(data));
}

export function isSetupComplete(profile: string): boolean {
  const data = getProfileSetup(profile);
  return data?.setupComplete === true && data?.goalStatus === 'active';
}

export function resetGoalCycle(profile: string): void {
  const data = getProfileSetup(profile);
  if (!data) return;
  // Keep body stats but reset goal and unlock setup
  const reset: ProfileSetupData = {
    ...data,
    goalType: 'weight_loss',
    goalTitle: '',
    goalTargetValue: '',
    goalTargetDate: '',
    setupComplete: false,
    goalStatus: 'active',
  };
  saveProfileSetup(profile, reset);
}

export default function HeroSetupForm({
  profileKey,
  profileName,
  accentClass,
  borderClass,
  onComplete,
  onCancel,
}: HeroSetupFormProps) {
  const [step, setStep] = useState(1);
  const [currentWeight, setCurrentWeight] = useState('');
  const [targetWeight, setTargetWeight] = useState('');
  const [heightCm, setHeightCm] = useState('');
  const [age, setAge] = useState('');
  const [gender, setGender] = useState('male');
  const [activityLevel, setActivityLevel] = useState('moderate');
  const [targetCalories, setTargetCalories] = useState('');
  const [targetProtein, setTargetProtein] = useState('');
  const [targetCarbs, setTargetCarbs] = useState('');
  const [targetFat, setTargetFat] = useState('');
  const [goalType, setGoalType] = useState('weight_loss');
  const [goalTitle, setGoalTitle] = useState('');
  const [goalTargetValue, setGoalTargetValue] = useState('');
  const [goalTargetDate, setGoalTargetDate] = useState('');
  const [saving, setSaving] = useState(false);

  const today = new Date().toISOString().split('T')[0];

  const canStep1 = currentWeight.trim() && targetWeight.trim() && heightCm.trim() && age.trim();
  const canStep2 = targetCalories.trim() && targetProtein.trim();
  const canStep3 = goalTitle.trim();

  function handleSubmit() {
    setSaving(true);
    const data: ProfileSetupData = {
      currentWeight,
      targetWeight,
      heightCm,
      age,
      gender,
      activityLevel,
      targetCalories,
      targetProtein,
      targetCarbs,
      targetFat,
      goalType,
      goalTitle,
      goalTargetValue,
      goalTargetDate,
      setupComplete: true,
      goalStatus: 'active',
    };
    saveProfileSetup(profileKey, data);
    setSaving(false);
    onComplete();
  }

  const stepLabels = ['Body Stats', 'Nutrition Targets', 'Forged Goal'];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm p-4">
      <div className="w-full max-w-md bg-card border border-border rounded-2xl overflow-hidden shadow-2xl">
        {/* Header */}
        <div className={`bg-gradient-to-r from-${accentClass}/20 to-transparent border-b border-border px-5 py-4`}>
          <div className="flex items-center gap-3 mb-3">
            <div className={`w-10 h-10 rounded-full bg-${accentClass}/10 border-2 border-${accentClass}/40 flex items-center justify-center`}>
              <span className={`text-xl font-black text-${accentClass}`}>{profileName.charAt(0)}</span>
            </div>
            <div>
              <div className={`text-xs text-${accentClass} font-bold tracking-widest uppercase`}>Mission Brief — Day 1</div>
              <h2 className="text-lg font-black tracking-wider text-foreground">{profileName.toUpperCase()}</h2>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mb-3">
            Complete your setup to enter the Forge. This data locks in until your goal is achieved or marked failed.
          </p>
          {/* Step bar */}
          <div className="flex gap-1.5 mb-1">
            {[1, 2, 3].map((s) => (
              <div
                key={s}
                className={`h-1 flex-1 rounded-full transition-all duration-300 ${s <= step ? `bg-${accentClass}` : 'bg-border'}`}
              />
            ))}
          </div>
          <div className="text-xs text-muted-foreground">
            Step {step} of 3 — <span className="text-foreground font-semibold">{stepLabels[step - 1]}</span>
          </div>
        </div>

        {/* Body */}
        <div className="p-5 max-h-[58vh] overflow-y-auto space-y-4">
          {/* STEP 1: Body Stats */}
          {step === 1 && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-muted-foreground font-bold uppercase tracking-wider block mb-1.5">Current Weight (kg)</label>
                  <input
                    type="number"
                    value={currentWeight}
                    onChange={(e) => setCurrentWeight(e.target.value)}
                    placeholder="e.g. 99"
                    inputMode="decimal"
                    className="w-full bg-input border border-border rounded-md px-3 py-2.5 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/30"
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground font-bold uppercase tracking-wider block mb-1.5">Target Weight (kg)</label>
                  <input
                    type="number"
                    value={targetWeight}
                    onChange={(e) => setTargetWeight(e.target.value)}
                    placeholder="e.g. 85"
                    inputMode="decimal"
                    className="w-full bg-input border border-border rounded-md px-3 py-2.5 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/30"
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground font-bold uppercase tracking-wider block mb-1.5">Height (cm)</label>
                  <input
                    type="number"
                    value={heightCm}
                    onChange={(e) => setHeightCm(e.target.value)}
                    placeholder="e.g. 178"
                    inputMode="numeric"
                    className="w-full bg-input border border-border rounded-md px-3 py-2.5 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/30"
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground font-bold uppercase tracking-wider block mb-1.5">Age</label>
                  <input
                    type="number"
                    value={age}
                    onChange={(e) => setAge(e.target.value)}
                    placeholder="e.g. 32"
                    inputMode="numeric"
                    className="w-full bg-input border border-border rounded-md px-3 py-2.5 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/30"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs text-muted-foreground font-bold uppercase tracking-wider block mb-2">Gender</label>
                <div className="grid grid-cols-2 gap-2">
                  {['male', 'female'].map((g) => (
                    <button
                      key={g}
                      type="button"
                      onClick={() => setGender(g)}
                      className={`py-2.5 rounded-md text-sm font-bold capitalize transition-all ${
                        gender === g ? 'bg-primary text-primary-foreground' : 'bg-input border border-border text-muted-foreground hover:border-primary/40'
                      }`}
                    >
                      {g}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs text-muted-foreground font-bold uppercase tracking-wider block mb-2">Activity Level</label>
                <div className="space-y-1.5">
                  {ACTIVITY_LEVELS.map((a) => (
                    <button
                      key={a.value}
                      type="button"
                      onClick={() => setActivityLevel(a.value)}
                      className={`w-full flex items-center justify-between px-3 py-2 rounded-md text-sm transition-all ${
                        activityLevel === a.value
                          ? 'bg-primary/15 border border-primary/50 text-foreground'
                          : 'bg-input border border-border text-muted-foreground hover:border-primary/30'
                      }`}
                    >
                      <span className="font-bold">{a.label}</span>
                      <span className="text-xs opacity-70">{a.desc}</span>
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* STEP 2: Nutrition Targets */}
          {step === 2 && (
            <>
              <p className="text-xs text-muted-foreground">Set your daily nutrition targets. These become your compliance benchmarks.</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <label className="text-xs text-muted-foreground font-bold uppercase tracking-wider block mb-1.5">Daily Calories (kcal)</label>
                  <input
                    type="number"
                    value={targetCalories}
                    onChange={(e) => setTargetCalories(e.target.value)}
                    placeholder="e.g. 2200"
                    inputMode="numeric"
                    className="w-full bg-input border border-border rounded-md px-3 py-2.5 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/30"
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground font-bold uppercase tracking-wider block mb-1.5">
                    <span className="text-primary">Protein</span> (g)
                  </label>
                  <input
                    type="number"
                    value={targetProtein}
                    onChange={(e) => setTargetProtein(e.target.value)}
                    placeholder="e.g. 180"
                    inputMode="numeric"
                    className="w-full bg-input border border-border rounded-md px-3 py-2.5 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/30"
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground font-bold uppercase tracking-wider block mb-1.5">
                    <span className="text-accent">Carbs</span> (g)
                  </label>
                  <input
                    type="number"
                    value={targetCarbs}
                    onChange={(e) => setTargetCarbs(e.target.value)}
                    placeholder="e.g. 200"
                    inputMode="numeric"
                    className="w-full bg-input border border-border rounded-md px-3 py-2.5 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/30"
                  />
                </div>
                <div className="col-span-2">
                  <label className="text-xs text-muted-foreground font-bold uppercase tracking-wider block mb-1.5">
                    <span className="text-amber-400">Fat</span> (g)
                  </label>
                  <input
                    type="number"
                    value={targetFat}
                    onChange={(e) => setTargetFat(e.target.value)}
                    placeholder="e.g. 65"
                    inputMode="numeric"
                    className="w-full bg-input border border-border rounded-md px-3 py-2.5 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/30"
                  />
                </div>
              </div>
              <div className="bg-card-elevated rounded-lg p-3 border border-border/50">
                <p className="text-xs text-muted-foreground">
                  <span className="text-primary font-bold">TIP:</span> For fat loss, aim for 1.8–2.2g protein per kg bodyweight. Create a 300–500 kcal daily deficit.
                </p>
              </div>
            </>
          )}

          {/* STEP 3: Goal */}
          {step === 3 && (
            <>
              <p className="text-xs text-muted-foreground">Define what you are fighting for. This goal locks in until achieved or failed.</p>
              <div>
                <label className="text-xs text-muted-foreground font-bold uppercase tracking-wider block mb-1.5">Goal Type</label>
                <select
                  value={goalType}
                  onChange={(e) => setGoalType(e.target.value)}
                  className="w-full bg-input border border-border rounded-md px-3 py-2.5 text-sm text-foreground focus:outline-none focus:border-primary/50"
                >
                  {GOAL_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs text-muted-foreground font-bold uppercase tracking-wider block mb-1.5">Goal Title *</label>
                <input
                  type="text"
                  value={goalTitle}
                  onChange={(e) => setGoalTitle(e.target.value)}
                  placeholder="e.g. Reach 85kg by August"
                  className="w-full bg-input border border-border rounded-md px-3 py-2.5 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/30"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-muted-foreground font-bold uppercase tracking-wider block mb-1.5">Target Value</label>
                  <input
                    type="number"
                    value={goalTargetValue}
                    onChange={(e) => setGoalTargetValue(e.target.value)}
                    placeholder={targetWeight || 'e.g. 85'}
                    inputMode="decimal"
                    className="w-full bg-input border border-border rounded-md px-3 py-2.5 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary/50"
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground font-bold uppercase tracking-wider block mb-1.5">Target Date</label>
                  <input
                    type="date"
                    value={goalTargetDate}
                    onChange={(e) => setGoalTargetDate(e.target.value)}
                    min={today}
                    className="w-full bg-input border border-border rounded-md px-3 py-2.5 text-sm text-foreground focus:outline-none focus:border-primary/50"
                  />
                </div>
              </div>
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
                <p className="text-xs text-amber-400 font-bold">⚠ LOCKED IN</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Once you begin, your body stats and nutrition targets are locked. Your goal can only be reset when you achieve it or mark it as failed.
                </p>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-border flex gap-3">
          {step === 1 ? (
            <button
              type="button"
              onClick={onCancel}
              className="btn-forge-ghost px-4 py-2.5 rounded-md text-sm font-bold"
            >
              CANCEL
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setStep((s) => s - 1)}
              className="btn-forge-ghost px-4 py-2.5 rounded-md text-sm font-bold"
            >
              BACK
            </button>
          )}
          {step < 3 ? (
            <button
              type="button"
              onClick={() => setStep((s) => s + 1)}
              disabled={step === 1 ? !canStep1 : !canStep2}
              className="flex-1 btn-forge-primary py-2.5 rounded-md text-sm font-bold disabled:opacity-40 disabled:cursor-not-allowed"
            >
              NEXT →
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={saving || !canStep3}
              className="flex-1 btn-forge-primary py-2.5 rounded-md text-sm font-bold disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {saving ? (
                <>
                  <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  FORGING...
                </>
              ) : (
                'BEGIN THE FORGE ⚡'
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
